<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import { lockDialogScroll } from "../../lib/dialog-scroll-lock.js";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  ariaLabel: { type: String, default: "" },
  ariaLabelledby: { type: String, default: "" },
  ariaDescribedby: { type: String, default: "" },
  role: { type: String, default: "dialog" },
  panelTag: { type: String, default: "div" },
  initialFocus: { type: String, default: "" },
  closeOnBackdrop: { type: Boolean, default: true },
  closeOnEscape: { type: Boolean, default: true },
  returnFocus: { type: Boolean, default: true },
  contained: { type: Boolean, default: false },
  lockScroll: { type: Boolean, default: false },
  padded: { type: Boolean, default: true },
  panelScroll: { type: Boolean, default: true },
  zIndex: { type: Number, default: 12000 },
  overlayClass: { type: [String, Array, Object], default: "" },
  panelClass: { type: [String, Array, Object], default: "" },
});

const emit = defineEmits(["update:modelValue", "close"]);

if (!props.ariaLabel && !props.ariaLabelledby) {
  throw new TypeError("UiDialog requires ariaLabel or ariaLabelledby");
}

const panelRef = ref(null);
let previousFocus = null;
let trackingFocus = false;
let releaseScroll;

watch(
  () => props.modelValue && props.lockScroll && !props.contained,
  (lock) => {
    releaseScroll?.();
    releaseScroll = lock ? lockDialogScroll() : null;
  },
  { immediate: true },
);

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function focusableElements() {
  if (!panelRef.value) return [];
  return Array.from(panelRef.value.querySelectorAll(focusableSelector)).filter(
    (element) =>
      element.getAttribute("aria-hidden") !== "true" &&
      !element.hasAttribute("hidden"),
  );
}

function focusInitialElement() {
  if (!panelRef.value) return;
  const target =
    (props.initialFocus && panelRef.value.querySelector(props.initialFocus)) ||
    focusableElements()[0] ||
    panelRef.value;
  target.focus();
}

async function startFocusManagement() {
  previousFocus = document.activeElement;
  trackingFocus = true;
  await nextTick();
  if (props.modelValue) focusInitialElement();
}

async function stopFocusManagement() {
  if (!trackingFocus) return;
  trackingFocus = false;
  const target = previousFocus;
  previousFocus = null;
  if (!props.returnFocus) return;
  await nextTick();
  target.focus();
}

function close(reason) {
  emit("update:modelValue", false);
  emit("close", reason);
}

function onBackdropClick() {
  if (props.closeOnBackdrop) close("backdrop");
}

function onKeydown(event) {
  if (event.key === "Escape") {
    if (!props.closeOnEscape) return;
    event.preventDefault();
    event.stopPropagation();
    close("escape");
    return;
  }
  if (event.key !== "Tab") return;
  const focusable = focusableElements();
  if (!focusable.length) {
    event.preventDefault();
    panelRef.value.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) void startFocusManagement();
    else void stopFocusManagement();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  releaseScroll?.();
  if (trackingFocus && props.returnFocus) previousFocus.focus();
  trackingFocus = false;
});
</script>

<template>
  <Teleport to="body" :disabled="contained">
    <Transition name="ui-dialog-fade">
      <div
        v-if="modelValue"
        class="ui-dialog-overlay inset-0 flex items-center justify-center overflow-y-auto bg-black/60 backdrop-blur-sm"
        :class="[
          contained ? 'absolute' : 'fixed',
          { 'ui-dialog-overlay--padded': padded },
          overlayClass,
        ]"
        :style="{ zIndex }"
        data-ui-dialog-overlay
        @click.self="onBackdropClick"
        @keydown="onKeydown"
      >
        <component
          :is="panelTag"
          ref="panelRef"
          v-bind="$attrs"
          :role="role"
          aria-modal="true"
          :aria-label="ariaLabel || undefined"
          :aria-labelledby="ariaLabelledby || undefined"
          :aria-describedby="ariaDescribedby || undefined"
          tabindex="-1"
          :class="[{ 'ui-dialog-panel--scroll': panelScroll }, panelClass]"
        >
          <slot :close="close" />
        </component>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.ui-dialog-overlay {
  --ui-dialog-inset-top: 0px;
  --ui-dialog-inset-right: 0px;
  --ui-dialog-inset-bottom: 0px;
  --ui-dialog-inset-left: 0px;
}

.ui-dialog-overlay--padded {
  --ui-dialog-inset-top: max(1.5rem, env(safe-area-inset-top, 0px));
  --ui-dialog-inset-right: max(1.5rem, env(safe-area-inset-right, 0px));
  --ui-dialog-inset-bottom: max(1.5rem, env(safe-area-inset-bottom, 0px));
  --ui-dialog-inset-left: max(1.5rem, env(safe-area-inset-left, 0px));
  padding: var(--ui-dialog-inset-top) var(--ui-dialog-inset-right)
    var(--ui-dialog-inset-bottom) var(--ui-dialog-inset-left);
}

.ui-dialog-panel--scroll {
  /* Safari's visible overlay can be shorter than the dynamic viewport. */
  max-height: min(
    100%,
    calc(100dvh - var(--ui-dialog-inset-top) - var(--ui-dialog-inset-bottom))
  );
  overflow-y: auto;
  overscroll-behavior: contain;
}

.ui-dialog-fade-enter-active,
.ui-dialog-fade-leave-active {
  transition: opacity 0.15s ease;
}

.ui-dialog-fade-enter-from,
.ui-dialog-fade-leave-to {
  opacity: 0;
}
</style>
